import { ChangeDetectorRef, Component, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { HttpService } from '../../http.service';
import { ActivatedRoute } from '@angular/router';

import { NgForm, Validators } from '@angular/forms';
import { ImageUploaderComponent } from '../../image-uploader/image-uploader.component';
import { promotionEditorValue } from '../../util/promotion-editor';

@Component({
  selector: 'app-front-promotion-form',
  templateUrl: './front-promotion-form.component.html',
  styleUrls: ['./front-promotion-form.component.scss']
})
export class FrontPromotionFormComponent {

  @ViewChild("f", {static: false}) f:NgForm;
  @ViewChildren(ImageUploaderComponent) imageUploaders: QueryList<ImageUploaderComponent>;
  public save_error = '';
  get is_uploading(): boolean {
    return this.imageUploaders?.some(uploader => uploader.is_loading) || false;
  }
  get upload_error(): string {
    return this.imageUploaders?.find(uploader => !!uploader.error)?.error || '';
  }
  public data: any = {calculation: 'fixed'};
  public loading: boolean = true;
  public games: any = [];
  public promotion_categories: any = [];
  public player_levels: any = [];
  public days: any = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  public is_loading_game: any = false;
  public is_saving: boolean = false;
  public editorValueSetter = promotionEditorValue;
  private editors: Record<string, any> = {};

  // --- Simulator inputs ---
  public simulatedDeposit: number = 100;
  public simulatedWalletBalance: number = 0;
  public simulatedLossAmount: number = 200;       // daily-lost-bonus / game-unlimted
  public simulatedReferralCount: number = 3;      // share-register-bonus types
  public simulatedReferralWallet: number = 50;    // referral-bonus
  public simulatedDownlineCount: number = 2;      // downline-commission-bonus

  // Types that never need a deposit input
  private readonly NO_DEPOSIT_TYPES = [
    'free-credit', 'daily-free-bonus', 'share-register-bonus-free-credit',
    'birthday-bonus', 'social-media-bonus', 'vip-bonus'
  ];

  // All types that the simulator now fully supports
  private readonly SUPPORTED_TYPES = [
    'free-credit', 'first-topup', 'new-register',
    'day-bonus', 'daily-checkin-bonus', 'daily-deposit-bonus',
    'daily-free-gift', 'daily-free-bonus', 'daily-lost-bonus', 'weekly-lost-bonus', 'monthly-lost-bonus',
    'weekly-bonus', 'monthly-bonus', 'happy-hour-bonus',
    'game-unlimted', 'vip-bonus', 'birthday-bonus', 'social-media-bonus',
    'share-register-bonus', 'share-register-bonus-free-credit',
    'referral-bonus', 'downline-commission-bonus'
  ];

  get isSimulatorSupported(): boolean {
    if (!this.data || !this.data.type) return false;
    return this.SUPPORTED_TYPES.includes(this.data.type);
  }

  get isDepositRequired(): boolean {
    if (this.data?.type === 'new-register' && Number(this.data.claim_without_deposit) === 1) return false;
    if (this.data && this.data.type) {
      return !this.NO_DEPOSIT_TYPES.includes(this.data.type);
    }
    return true;
  }

  /** True when the simulator needs a "loss amount" input instead of deposit */
  get needsLossInput(): boolean {
    return ['daily-lost-bonus', 'weekly-lost-bonus', 'monthly-lost-bonus', 'game-unlimted'].includes(this.data?.type);
  }

  /** True when the simulator needs a "referral count" input */
  get needsReferralCountInput(): boolean {
    return ['share-register-bonus', 'share-register-bonus-free-credit'].includes(this.data?.type);
  }

  /** True when the simulator needs a "referral wallet balance" input */
  get needsReferralWalletInput(): boolean {
    return this.data?.type === 'referral-bonus';
  }

  /** True when the simulator needs a "downline count" input */
  get needsDownlineInput(): boolean {
    return this.data?.type === 'downline-commission-bonus';
  }

  get simulatedBonus(): number {
    if (!this.data || this.data.bonus == null) return 0;
    const bonusVal = Number(this.data.bonus);
    if (isNaN(bonusVal)) return 0;

    const type = this.data.type;

    // referral-bonus: bonus = full referral wallet balance (transferred in)
    if (type === 'referral-bonus') {
      return this.simulatedReferralWallet;
    }

    // share-register types: count × per-referral bonus
    if (type === 'share-register-bonus' || type === 'share-register-bonus-free-credit') {
      if (this.data.calculation === 'share-register') {
        return this.simulatedReferralCount * bonusVal;
      }
      return this.simulatedReferralCount * bonusVal; // fallback
    }

    // downline-commission-bonus: per downline × count (fixed or percentage of their deposit)
    if (type === 'downline-commission-bonus') {
      if (this.data.calculation === 'percentage') {
        let b = (this.simulatedDeposit * bonusVal) / 100;
        if (this.data.max_bonus) b = Math.min(b, Number(this.data.max_bonus));
        return b * this.simulatedDownlineCount;
      }
      return bonusVal * this.simulatedDownlineCount;
    }

    // No calculation field for vip/birthday/social (always fixed credit stored in bonus)
    if (['vip-bonus', 'birthday-bonus', 'social-media-bonus'].includes(type)) {
      return bonusVal;
    }

    if (this.data.calculation === 'fixed') {
      return bonusVal;
    }

    if (this.data.calculation === 'percentage') {
      // Base depends on type
      let baseAmount: number;
      if (type === 'daily-lost-bonus' || type === 'game-unlimted') {
        baseAmount = this.simulatedLossAmount;
      } else if (this.NO_DEPOSIT_TYPES.includes(type)) {
        baseAmount = this.simulatedWalletBalance;
      } else {
        baseAmount = this.simulatedDeposit;
      }
      let b = (baseAmount * bonusVal) / 100;
      if (this.data.max_bonus != null) {
        const max = Number(this.data.max_bonus);
        if (!isNaN(max) && max > 0 && b > max) b = max;
      }
      return b;
    }

    return 0;
  }

  get simulatedWalletAfterBonus(): number {
    return this.simulatedWalletBalance + this.simulatedBonus;
  }

  get simulatedTarget(): number {
    if (!this.data || !this.data.calculate_by || this.data.min_trigger == null) return 0;
    const trigger = Number(this.data.min_trigger);
    if (isNaN(trigger)) return 0;

    const depositAmount = this.isDepositRequired && !this.needsLossInput ? this.simulatedDeposit : 0;

    if (this.data.calculate_by === 'turnover' || this.data.calculate_by === 'rolling') {
      return (depositAmount + this.simulatedWalletBalance + this.simulatedBonus) * trigger;
    }
    if (this.data.calculate_by === 'turnover-fixed' || this.data.calculate_by === 'rolling-fixed') {
      return trigger;
    }
    // Wallet Balance: target is the threshold itself — player withdraws when wallet reaches this amount.
    if (this.data.calculate_by === 'wallet-balance') {
      return trigger;
    }
    return 0;
  }

  /** Resolved minimum withdrawal amount, mirrors API resolveMinWithdraw() logic */
  get simulatedMinWithdraw(): number {
    const raw = Number(this.data?.min_withdraw) || 0;
    if (!raw) return 0;
    const mode = this.data?.min_withdraw_type || 'fixed';
    if (mode === 'percentage-bonus') {
      return Math.round((this.simulatedBonus * raw / 100) * 100) / 100;
    }
    if (mode === 'percentage-wallet-bonus') {
      return Math.round((this.simulatedWalletAfterBonus * raw / 100) * 100) / 100;
    }
    return raw; // fixed
  }

  get simulatedMinWithdrawLabel(): string {
    const mode = this.data?.min_withdraw_type || 'fixed';
    const raw = this.data?.min_withdraw;
    if (mode === 'percentage-bonus') return `${raw}% of Bonus (RM ${this.simulatedBonus.toFixed(2)})`;
    if (mode === 'percentage-wallet-bonus') return `${raw}% of Wallet+Bonus (RM ${this.simulatedWalletAfterBonus.toFixed(2)})`;
    return 'Fixed';
  }

  /** True if simulated profit (wallet after - wallet before) would exceed max_withdraw */
  get maxWithdrawExceeded(): boolean {
    if (!this.data?.max_withdraw || Number(this.data.max_withdraw) <= 0) return false;
    return this.simulatedBonus > Number(this.data.max_withdraw);
  }

  get eligibilityStatus(): { eligible: boolean, message: string } {
    const type = this.data?.type;

    // Deposit minimum check
    if (this.isDepositRequired && !this.needsLossInput && this.data.min_deposit &&
        this.simulatedDeposit < Number(this.data.min_deposit)) {
      return { eligible: false, message: `Deposit RM ${this.simulatedDeposit} is below minimum RM ${this.data.min_deposit}` };
    }

    // Wallet balance cap check
    if (this.data.max_wallet_balance && Number(this.data.max_wallet_balance) !== 0 && this.simulatedWalletBalance > Number(this.data.max_wallet_balance)) {
      return { eligible: false, message: `Wallet RM ${this.simulatedWalletBalance} exceeds max allowed RM ${this.data.max_wallet_balance}` };
    }

    // Loss minimum check
    if (this.needsLossInput && this.data.min_lose &&
        this.simulatedLossAmount < Number(this.data.min_lose)) {
      return { eligible: false, message: `Loss RM ${this.simulatedLossAmount} is below minimum required RM ${this.data.min_lose}` };
    }

    // Happy hour window check (uses current browser time as approximation)
    if (type === 'happy-hour-bonus' && this.data.from_happy_hour && this.data.to_happy_hour) {
      const now = new Date();
      const myt = new Date(now.getTime() + 8 * 60 * 60 * 1000); // UTC+8
      const nowMins = myt.getUTCHours() * 60 + myt.getUTCMinutes();
      const [fh, fm] = this.data.from_happy_hour.split(':').map(Number);
      const [th, tm] = this.data.to_happy_hour.split(':').map(Number);
      const fromMins = fh * 60 + fm;
      const toMins = th * 60 + tm;
      if (nowMins < fromMins || nowMins >= toMins) {
        return { eligible: false, message: `Outside happy hour window (${this.data.from_happy_hour} – ${this.data.to_happy_hour} MYT)` };
      }
    }

    // Day-of-week check for day-bonus
    if (type === 'day-bonus' && this.data.day?.length) {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const today = dayNames[new Date().getDay()];
      if (!this.data.day.includes(today)) {
        return { eligible: false, message: `Today (${today}) is not in the allowed days: ${this.data.day.join(', ')}` };
      }
    }

    // Referral wallet minimum check
    if (type === 'referral-bonus' && this.data.min_deposit &&
        this.simulatedReferralWallet < Number(this.data.min_deposit)) {
      return { eligible: false, message: `Referral wallet RM ${this.simulatedReferralWallet} is below minimum RM ${this.data.min_deposit}` };
    }

    return { eligible: true, message: 'Meets all conditions' };
  }

  get promotionTypeDisplay(): string {
    if (!this.data || !this.data.type) return 'Select Promotion Type';
    
    const types: {[key: string]: string} = {
      'free-credit': 'Free Credit (No Deposit Needed)',
      'first-topup': 'Welcome Bonus',
      'day-bonus': 'Day Bonus',
      'daily-checkin-bonus': 'Daily Check In Bonus',
      'daily-deposit-bonus': 'Daily Deposit Bonus',
      'daily-free-gift': 'Daily Free Gift',
      'monthly-bonus': 'Monthly Bonus',
      'weekly-bonus': 'Weekly Bonus',
      'downline-commission-bonus': 'Downline Commission Bonus',
      'new-register': 'New Register',
      'happy-hour-bonus': 'Happy Hour Bonus',
      'daily-lost-bonus': 'Daily Lost Bonus',
      'weekly-lost-bonus': 'Weekly Lost Bonus',
      'monthly-lost-bonus': 'Monthly Lost Bonus',
      'daily-free-bonus': 'Daily Free Bonus',
      'game-unlimted': 'Game Unlimited',
      'share-register-bonus': 'Share Register Bonus',
      'share-register-bonus-free-credit': 'Share Register Bonus (No Deposit)',
      'referral-bonus': 'Referral Bonus',
      'vip-bonus': 'VIP Bonus',
      'birthday-bonus': 'Birthday Bonus',
      'social-media-bonus': 'Social Media Bonus'
    };
    return types[this.data.type] || this.data.type;
  }

  constructor(private http: HttpService, private activatedRoute: ActivatedRoute, private cdr: ChangeDetectorRef) { 
    this.activatedRoute.params.subscribe(params => {
      this.init(params['promotion_uuid']);
    });
    this.getGames();
  }

  async init(promotion_uuid = null) {
      try {
          this.loading = true;
          this.editors = {};
          this.cdr.markForCheck();

          // Category + player-level lists are needed for BOTH new and existing
          // promotions, so load them before any early return. Previously these
          // only loaded when editing, leaving the category picker empty ("no
          // item found") on the new-promotion form.
          this.promotion_categories = await this.http.post('/admin/app/promotion-category/get', {status: 1});
          this.player_levels = await this.http.post('/admin/setting/player-level/get');

          if (!promotion_uuid) {
              this.data = {calculation: 'fixed'};
              return;
          }

          let response: any = await this.http.post('/admin/app/promotion', {promotion_uuid});
          console.log("Promotion API Response:", response);
          let row: any = response;

          // The API returns the promotion object directly (not wrapped)
          if (!row || typeof row !== 'object') {
              console.error("Invalid promotion data received:", response);
              this.data = {calculation: 'fixed'};
              this.loading = false;
              this.cdr.detectChanges();
              return;
          }

          console.log("Promotion Row:", row);
          console.log("description_delta:", row.description_delta);
          console.log("tnc_delta:", row.tnc_delta);

          // Ensure delta fields exist even if the API only returns 'description' or 'tnc'
          if (!row.description_delta && row.description) {
              row.description_delta = row.description;
          }
          if (!row.tnc_delta && row.tnc) {
              row.tnc_delta = row.tnc;
          }

          // Safe parse using optional chaining to prevent crashes
          if (typeof row.description_delta === 'string' && (row.description_delta.startsWith('{') || row.description_delta.startsWith('['))) {
            try { row.description_delta = JSON.parse(row.description_delta); } catch (e) { }
          }
          if (typeof row.tnc_delta === 'string' && (row.tnc_delta.startsWith('{') || row.tnc_delta.startsWith('['))) {
            try { row.tnc_delta = JSON.parse(row.tnc_delta); } catch (e) { }
          }
          if (typeof row.player_level_id === 'string' && row.player_level_id.startsWith('[')) {
            try { row.player_level_id = JSON.parse(row.player_level_id); } catch (e) { }
          }
          if (typeof row.game_id === 'string' && row.game_id.startsWith('[')) {
            try { row.game_id = JSON.parse(row.game_id); } catch (e) { }
          }
          if (typeof row.day === 'string' && row.day.startsWith('[')) {
            try { row.day = JSON.parse(row.day); } catch (e) { }
          }

          this.data = row;
          console.log("Final data object:", this.data);
          console.log("Final description_delta:", this.data.description_delta);
          console.log("Final tnc_delta:", this.data.tnc_delta);
      } catch (e) {
          console.error("Promotion form init error:", e);
      } finally {
          this.loading = false;
          this.cdr.markForCheck();
      }
  }

  editorCreated(quill: any, param: string) {
    this.editors[param] = quill;
    // ngx-quill has already applied editorValueSetter before this event.
    this.syncEditor(param);
    quill.on('text-change', (_delta: any, _old: any, source: string) => {
      if (source !== 'user') return;
      this.syncEditor(param);
      this.cdr.markForCheck();
    });
    this.cdr.markForCheck();
  }

  private syncEditor(param: string) {
    const quill = this.editors[param];
    if (!quill) return;
    this.data[param] = quill.getContents();
    this.data[param.replace('_delta', '')] = quill.root.innerHTML;
  }

  async submit() {
    if (this.is_saving) return;
    this.save_error = '';
    if (this.is_uploading || this.upload_error) {
      this.save_error = this.upload_error || 'Please wait for the image upload to finish before saving.';
      return;
    }
    if (!this.editors.description_delta || !this.editors.tnc_delta) {
      this.save_error = 'Please wait for the promotion editors to finish loading.';
      return;
    }
    // Read the current editor values at submit, including untouched legacy HTML.
    Object.keys(this.editors).forEach(param => this.syncEditor(param));
    if(this.f.invalid) {
      const invalidFields = Object.keys(this.f.controls)
        .filter(name => this.f.controls[name].invalid)
        .map(name => name.replace(/_/g, ' '));
      this.save_error = `Please complete the required fields: ${invalidFields.join(', ')}.`;
      this.http.notify(this.save_error);
      return;
    }

    if ((this.data.to_date && this.data.from_date > this.data.to_date)) {
      this.save_error = 'End date must be on or after the start date.';
      this.http.notify(this.save_error);
      return
    }

    this.is_saving = true;
    try {
      const s: any = await this.http.post("/admin/app/promotion/v2/update", this.data)
      if (s?.status) {
        this.http.notify("Promotion updated successfully.")
        this.http.naviBack();
        return;
      }
      this.save_error = s?.message || 'Promotion could not be saved. Please try again.';
      this.http.notify(this.save_error);
    } catch (error) {
      this.save_error = 'Promotion could not be saved. Please try again.';
      this.http.notify(this.save_error);
    } finally {
      this.is_saving = false;
    }
  }

  async loadFile(fileInput: any, name: string) {
    this.data[name] = await this.http.fileChangeEvent(fileInput);
  }

  upload(data:any, key:string, single = false) {
    this.save_error = '';
    this.data[key] = (single) ? data[0] : data
  }

  delete(data:any, key:string) {
    this.data[key] = Array.isArray(data) ? (data[0] || null) : data;
  }

  
  async getGames() {
    if(this.games && this.games.length > 0) {
      return;
    }
    
    this.is_loading_game = true;
    this.games = await this.http.post("/admin/setting/seemless-game/get/active")

    this.is_loading_game = false;
  }

}
